import mapboxgl from "mapbox-gl";

const LAT_LIMIT = 90;
const LNG_LIMIT = 180;

export function jitterMarkers(markers, spiralStep = 0.000005) {
  /* Jitter markers to avoid overlap by slightly adjusting their coordinates
   * - Groups markers with the same coordinates
   * - For single markers, keeps original coordinates
   * - For multiple markers, arranges them in a circle (2–5 points)
   *  or a spiral (6+ points) around the original coordinates
   * @param {Array} markers - Array of marker objects with coords
   * @param {number} spiralStep - Step size for spiral layout
   * @returns {Array} - New array of markers with adjusted coordinates
   */

  const coordMap = new Map();
  const result = [];
  const spiralAngleStep = Math.PI / 8;

  markers.forEach((m) => {
    if (
      m.coords.lat > -LAT_LIMIT &&
      m.coords.lat < LAT_LIMIT &&
      m.coords.lng > -LNG_LIMIT &&
      m.coords.lng < LNG_LIMIT
    ) {
      const key = `${m.coords.lat.toFixed(6)},${m.coords.lng.toFixed(6)}`;
      const group = coordMap.get(key) || [];
      group.push(m);
      coordMap.set(key, group);
    }
  });

  for (const group of coordMap.values()) {
    const original = group[0].coords;
    const count = group.length;

    if (count === 1) {
      result.push({ ...group[0], originalCoords: { ...original } });
      continue;
    }

    // Radius for evenly spaced circle layout (2–5 points)
    const circleRadius = 0.0001;

    group.forEach((m, i) => {
      let offsetLat, offsetLng;

      if (count <= 5) {
        // Circle layout
        const angle = (2 * Math.PI * i) / count;
        offsetLat = original.lat + circleRadius * Math.sin(angle);
        offsetLng = original.lng + circleRadius * Math.cos(angle);
      } else {
        // Spiral layout
        const angle = i * spiralAngleStep;
        const spiralRadius = circleRadius + spiralStep * (i + 1);

        offsetLat = original.lat + (spiralRadius / 2) * Math.sin(angle);
        offsetLng = original.lng + spiralRadius * Math.cos(angle);
      }

      offsetLat = Math.max(-LAT_LIMIT, Math.min(LAT_LIMIT, offsetLat));
      offsetLng = Math.max(-LNG_LIMIT, Math.min(LNG_LIMIT, offsetLng));

      result.push({
        ...m,
        coords: { lat: offsetLat, lng: offsetLng },
        originalCoords: { ...original },
      });
    });
  }

  return result;
}

export function createGeoJSONFromMarkers(markers) {
  /* Create GeoJSON FeatureCollection from markers
   * @param {Array} markers - Array of marker objects with coords
   * @returns {Object} - GeoJSON FeatureCollection
   */
  return {
    type: "FeatureCollection",
    features: markers.map((m) => ({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [m.coords.lng, m.coords.lat],
      },
      properties: {
        id: m.device?.device_id ?? null,
        name: m.device?.name ?? null,
        networkName: m.device?.network_name ?? null,
        statusLabel: m.deviceStatus?.label ?? null,
        statusColor: m.deviceStatus?.color ?? null,
        showInfoWindow: String(m.showInfoWindow === true),
      },
    })),
  };
}

export function createSpiderLines(markers) {
  /* Create GeoJSON FeatureCollection for spider lines
   * connecting original and jittered coordinates of markers
   * @param {Array} markers - Array of marker objects with coords and originalCoords
   * @returns {Object} - GeoJSON FeatureCollection for spider lines
   */
  return {
    type: "FeatureCollection",
    features: markers
      .filter(
        (m) =>
          m.originalCoords.lat !== m.coords.lat ||
          m.originalCoords.lng !== m.coords.lng,
      )
      .map((m) => ({
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: [
            [m.originalCoords.lng, m.originalCoords.lat],
            [m.coords.lng, m.coords.lat],
          ],
        },
      })),
  };
}

export function fitMapToFeatures(map, features, maxZoom) {
  /* Fit the map to the bounds of the provided features
   * - Uses LngLatBounds to calculate the bounding box
   * - Fits the map view to include all features with padding
   * @param {mapboxgl.Map}
   * @param {Array} features - Array of GeoJSON features with coordinates
   * @param {number} maxZoom - Maximum zoom level to apply
   */

  if (!Array.isArray(features) || features.length === 0) {
    return;
  }

  const bounds = features.reduce(
    (b, f) => b.extend(f.geometry.coordinates),
    new mapboxgl.LngLatBounds(
      features[0].geometry.coordinates,
      features[0].geometry.coordinates,
    ),
  );

  map.fitBounds(bounds, {
    duration: 1000,
    maxZoom,
    padding: 20,
  });
}

function createMapControl(renderFn, onRemoveFn) {
  let container;
  return {
    onAdd: () => {
      container = renderFn();
      return container;
    },
    onRemove: () => {
      onRemoveFn?.(container);
      container?.remove();
    },
  };
}

export function addResetControl(map, onReset) {
  /* Add a reset control to the map
   * - Creates a button that resets the map view to the initial state
   * @param {mapboxgl.Map}
   * @param {Function} onReset - Callback function to call when the button is clicked
   */
  map.addControl(
    createMapControl(() => {
      const btn = document.createElement("button");
      btn.className = "mapboxgl-ctrl-icon";
      btn.type = "button";
      btn.title = "Reset View";
      btn.innerHTML = `<i class="fa fa-globe"></i>`;
      btn.onclick = onReset;

      const container = document.createElement("div");
      container.className = "mapboxgl-ctrl-group mapboxgl-ctrl";
      container.appendChild(btn);
      return container;
    }),
    "top-left",
  );
}

export function addFilterControl(map, onFilterChange) {
  /* Add a filter control to the map
   * - Creates a set of radio buttons to filter markers by status
   * @param {mapboxgl.Map}
   * @param {Function} onFilterChange - Callback function to call when the filter changes
   */
  map.addControl(
    createMapControl(() => {
      const container = document.createElement("div");
      container.className = "mapboxgl-ctrl mapboxgl-ctrl-group filter-control";
      container.innerHTML = `
        <fieldset>
          <legend>Device Status</legend>
          <label>
            <input type="radio" name="statusFilter" value="all" checked>
            <span class="flex-grow-1">All</span>
          </label>
          <label>
            <input type="radio" name="statusFilter" value="online">
            <span class="flex-grow-1">Online</span>
            <span class="status-icon online"></span>
          </label>
          <label>
            <input type="radio" name="statusFilter" value="offline">
            <span class="flex-grow-1">Offline</span>
            <span class="status-icon offline"></span>
          </label>
        </fieldset>
      `;

      container.addEventListener("change", (e) => {
        if (e.target.name === "statusFilter") {
          onFilterChange(e.target.value);
        }
      });

      return container;
    }),
    "bottom-right",
  );
}
